import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { useNotification } from '@/ui/Notification'
import { useGetUserInfo } from '@/modules/Auth'
import { AllocationChange } from '@/types/vehicleAllocation'

interface IUpdateVehicleAllocationRequest {
    author: string
    route: string
    changes: AllocationChange[]
}

interface IUpdateVehicleAllocationResponse {}

export const useUpdateVehicleAllocation = () => {
    const queryClient = useQueryClient()
    const { showNotification } = useNotification()
    const { data: userInfo } = useGetUserInfo()

    const updateVehicleAllocationMutation = useAppMutation<
        IUpdateVehicleAllocationResponse,
        Error,
        IUpdateVehicleAllocationRequest
    >({
        mutationFn: (data) =>
            createInstance({
                url: '/fleets/vehicle-allocation',
                method: 'POST',
                data,
            }),
        alertOnError: true,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['GET_VEHICLE_ALLOCATION'] })
            showNotification({
                message: 'Updated successfully',
            })
        },
    })

    const handleUpdateVehicleAllocation = (data: Pick<IUpdateVehicleAllocationRequest, 'route' | 'changes'>) => {
        if (!userInfo) throw new Error('User info not found')

        updateVehicleAllocationMutation.mutate({
            author: userInfo.lastSessionDetails.authorName,
            route: data.route,
            changes: data.changes,
        })
    }

    return { handleUpdateVehicleAllocation, isPending: updateVehicleAllocationMutation.isPending }
}
